import { createApi } from "@reduxjs/toolkit/query/react";

import {
    ICollectClientsAssessmentsBody,
    ICollectClientsAssessmentsResponse,
    ICreateDemoProjectBody,
    ICreateDemoProjectResponse,
    ICreateProjectBody,
    ICreateProjectResponse,
    IDeleteProjectBody,
    IDeleteProjectResponse,
    IGetProjectByIdQuery,
    IGetProjectByIdResponse,
    IGetProjectEmailReportsQuery,
    IGetProjectEmailReportsResponse,
    IGetProjectPartnersSummaryResponse,
    IGetProjectNotificationsQuery,
    IGetProjectNotificationsResponse,
    IGetProjectReviewsQuery,
    IGetProjectReviewsResponse,
    IGetProjectsQuery,
    IGetProjectsResponse,
    IRecalculateImprovementsBody,
    IRecalculateImprovementsResponse,
    IRecalculateRecalculateUsageRetentionBody,
    ISendProjectNotificationBody,
    ISetPinnedProjectBody,
    ISetPinnedProjectResponse,
    ISetProjectManagersBody,
    ISetProjectManagersResponse,
    ISetProjectManagerTokensBody,
    ISetProjectManagerTokensResponse,
    IUpdateProjectBody,
    IUpdateProjectResponse,
} from "../../types/projects";
import { getProjectsQueryString } from "../../utils/queries";
import { authApi } from "../auth/api";
import { baseQueryWithIntercept } from "../connection/api";
import { usersApi } from "../users/api";

const ENDPOINT_PREFIX = "projects";

export const projectsApi = createApi({
    reducerPath: "projectsApi",
    baseQuery: baseQueryWithIntercept,
    tagTypes: ["Projects", "Project", "ProjectNotifications", "ProjectEmailReports", "ProjectPartnersSummary"],
    endpoints: build => ({
        getProjects: build.query<IGetProjectsResponse, IGetProjectsQuery>({
            query: query => ({
                url: `${ENDPOINT_PREFIX}?${getProjectsQueryString(query)}`,
            }),
            providesTags: (result, _error, _arg) =>
                result?.data.projects ? [...result.data.projects.map(({ id }) => ({ type: "Projects" as const, id })), "Projects"] : ["Projects"],
        }),
        createProject: build.mutation<ICreateProjectResponse, ICreateProjectBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/`,
                method: "POST",
                body: data.data,
            }),
            invalidatesTags: ["Projects"],
        }),
        createDemoProject: build.mutation<ICreateDemoProjectResponse, ICreateDemoProjectBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/demo`,
                method: "POST",
                body: data.data,
            }),
            invalidatesTags: ["Projects"],
        }),
        getProjectById: build.query<IGetProjectByIdResponse, IGetProjectByIdQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.projectId}${data.myTokensOnly ? "?myTokensOnly=true" : ""}`,
                method: "GET",
            }),
            providesTags: (_result, _error, arg) => [{ type: "Project", id: arg.projectId }],
        }),
        getProjectReviews: build.query<IGetProjectReviewsResponse, IGetProjectReviewsQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/reviews/${data.projectId}`,
                method: "GET",
            }),
            providesTags: (_result, _error, arg) => [{ type: "Project", id: arg.projectId }],
        }),
        getProjectPartnersSummary: build.query<IGetProjectPartnersSummaryResponse, { projectId: string }>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/project-partners-summary/${data.projectId}`,
                method: "GET",
            }),
            providesTags: (_result, _error, arg) => [{ type: "ProjectPartnersSummary", id: arg.projectId }],
        }),
        updateProject: build.mutation<IUpdateProjectResponse, IUpdateProjectBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.projectId}`,
                method: "PUT",
                body: data.data,
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Project", id: arg.projectId }, "Projects"],
        }),
        recalculateImprovements: build.mutation<IRecalculateImprovementsResponse, IRecalculateImprovementsBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/recalculate-improvements/${data.projectId}`,
                method: "POST",
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Project", id: arg.projectId }, "Projects"],
        }),
        recalculateCommittedClients: build.mutation<IRecalculateImprovementsResponse, IRecalculateImprovementsBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/recalculate-committed-clients/${data.projectId}`,
                method: "POST",
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Project", id: arg.projectId }, "Projects"],
        }),
        recalculateContinuedUsageRetentionByPeriod: build.mutation<void, IRecalculateRecalculateUsageRetentionBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/recalculate-usage-retention/${data.projectId}`,
                method: "POST",
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Project", id: arg.projectId }, "Projects"],
        }),
        getProjectFcmTokenClientsCount: build.query<any, IGetProjectNotificationsQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/project-fcm-token-clients-count/${data.projectId}`,
                method: "GET",
            }),
        }),
        getProjectNotifications: build.query<IGetProjectNotificationsResponse, IGetProjectNotificationsQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/project-notifications/${data.projectId}`,
                method: "GET",
            }),
            providesTags: (_result, _error, arg) => [{ type: "ProjectNotifications", id: arg.projectId }],
        }),

        sendProjectNotification: build.mutation<any, ISendProjectNotificationBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/send-project-notification/${data.projectId}`,
                method: "POST",
                body: data.data,
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "ProjectNotifications", id: arg.projectId }],
        }),
        setPinnedProject: build.mutation<ISetPinnedProjectResponse, ISetPinnedProjectBody>({
            query: data => ({
                url: "auth/pinned-projects",
                method: "PATCH",
                body: data,
            }),
            // The pinned list lives on the current user, whose cache belongs to authApi —
            // refresh it so user-data stays in sync with the pinned/unpinned split.
            async onQueryStarted(_data, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(authApi.util.invalidateTags(["Auth"]));
                } catch {
                    // pin failed — nothing to refresh
                }
            },
            invalidatesTags: ["Projects"],
        }),
        setProjectManagers: build.mutation<ISetProjectManagersResponse, ISetProjectManagersBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/set-project-managers/${data.projectId}`,
                method: "PATCH",
                body: {
                    managerIds: data.managerIds,
                },
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Project", id: arg.projectId }, "Projects"],
        }),
        setProjectManagerTokens: build.mutation<ISetProjectManagerTokensResponse, ISetProjectManagerTokensBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/set-project-manager-tokens/${data.projectId}`,
                method: "PATCH",
                body: {
                    managerId: data.managerId,
                    tokens: data.tokens,
                },
            }),
            // The managers table rows live in usersApi's cache (a separate createApi), which invalidatesTags
            // can't reach — refresh them so a reopened AssociateTokensModal pre-fills from the saved association.
            async onQueryStarted(data, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(
                        usersApi.util.invalidateTags([
                            { type: "Users", id: data.managerId },
                            { type: "User", id: data.managerId },
                        ])
                    );
                } catch {
                    // save failed — nothing to refresh
                }
            },
            invalidatesTags: (_result, _error, arg) => [{ type: "Project", id: arg.projectId }, "Projects"],
        }),
        setProjectPartners: build.mutation<any, { projectId: string; partnerIds: string[] }>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/set-project-partners/${data.projectId}`,
                method: "PATCH",
                body: {
                    partnerIds: data.partnerIds,
                },
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: "Project", id: arg.projectId },
                { type: "ProjectPartnersSummary", id: arg.projectId },
                "Projects",
            ],
        }),
        deleteProject: build.mutation<IDeleteProjectResponse, IDeleteProjectBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.id}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Project", id: arg.id }, "Projects"],
        }),
        collectClientsAssessments: build.mutation<ICollectClientsAssessmentsResponse, ICollectClientsAssessmentsBody>({
            query: ({ projectId, data }) => ({
                url: `${ENDPOINT_PREFIX}/collect-clients-assessments/${projectId}`,
                method: "POST",
                body: data,
            }),
        }),
        sendWeeklyReport: build.mutation<{ ok: boolean }, { projectId: string }>({
            query: ({ projectId }) => ({
                url: `${ENDPOINT_PREFIX}/send-weekly-report/${projectId}`,
                method: "POST",
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "ProjectEmailReports", id: arg.projectId }],
        }),
        getProjectEmailReports: build.query<IGetProjectEmailReportsResponse, IGetProjectEmailReportsQuery>({
            query: ({ projectId, type = "weekly report", latest = false }) => {
                const params = new URLSearchParams({ type });
                if (latest) params.append("latest", "true");

                return {
                    url: `${ENDPOINT_PREFIX}/project-email-reports/${projectId}?${params.toString()}`,
                    method: "GET",
                };
            },
            providesTags: (_result, _error, arg) => [{ type: "ProjectEmailReports", id: arg.projectId }],
        }),
        getWeeklyReportSchedule: build.query<
            { id: string; name: string; repeatInterval: string; repeatTimezone?: string; nextRunAt?: string; lastRunAt?: string },
            { projectId: string }
        >({
            query: ({ projectId }) => ({
                url: `jobs/project-weekly-report-schedule/${projectId}`,
                method: "GET",
            }),
            providesTags: (_res, _err, arg) => [{ type: "ProjectEmailReports", id: arg.projectId }],
        }),
        setWeeklyReportSchedule: build.mutation<
            { id: string; name: string; repeatInterval: string; repeatTimezone?: string; nextRunAt?: string; lastRunAt?: string },
            { projectId: string; data: { weekday: string; time: string; timezone: string } }
        >({
            query: ({ projectId, data }) => ({
                url: `jobs/project-weekly-report-schedule/${projectId}`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (_res, _err, arg) => [{ type: "ProjectEmailReports", id: arg.projectId }],
        }),
        deleteWeeklyReportSchedule: build.mutation<{ ok: boolean; cancelled: number }, { projectId: string }>({
            query: ({ projectId }) => ({
                url: `jobs/project-weekly-report-schedule/${projectId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_res, _err, arg) => [{ type: "ProjectEmailReports", id: arg.projectId }],
        }),
    }),
});

export const {
    useGetProjectsQuery,
    useGetProjectNotificationsQuery,
    useGetProjectFcmTokenClientsCountQuery,
    useCreateProjectMutation,
    useCreateDemoProjectMutation,
    useGetProjectByIdQuery,
    useGetProjectReviewsQuery,
    useGetProjectPartnersSummaryQuery,
    useUpdateProjectMutation,
    useSetPinnedProjectMutation,
    useSetProjectManagersMutation,
    useSetProjectManagerTokensMutation,
    useSetProjectPartnersMutation,
    useDeleteProjectMutation,
    useRecalculateImprovementsMutation,
    useRecalculateCommittedClientsMutation,
    useSendProjectNotificationMutation,
    useRecalculateContinuedUsageRetentionByPeriodMutation,
    useCollectClientsAssessmentsMutation,
    useSendWeeklyReportMutation,
    useGetProjectEmailReportsQuery,
    useGetWeeklyReportScheduleQuery,
    useSetWeeklyReportScheduleMutation,
    useDeleteWeeklyReportScheduleMutation,
} = projectsApi;
